import React from 'react';
import { Card, Flex, Tag, Typography, theme } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import InternshipApplicationComponent from './InternshipApplicationComponent';
import type { ApplyableStatus } from './InternshipApplicationComponent';
import { ImageLoader } from './ImageLoader';
import type { ReducedInternship } from '../../Types/interfaces';

interface InternshipOfferCardProps {
  internship: ReducedInternship;
  organizationName: string;
  sessionId: number;
  studyLineName: string;
  applyable: ApplyableStatus;
  removeInternship: (id: number) => void;
}

// plain CSS truncation; the antd ellipsis + tooltip config loops on re-render
const truncate: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const InternshipOfferCard = ({
  internship,
  organizationName,
  sessionId,
  studyLineName,
  applyable,
  removeInternship,
}: InternshipOfferCardProps): React.ReactElement => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <Card
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: token.boxShadowTertiary,
      }}
      styles={{ body: { padding: 16 } }}
      cover={
        <div
          style={{
            position: 'relative',
            height: 240,
            background: token.colorFillQuaternary,
          }}
        >
          <ImageLoader logoData={internship.logoData} />
        </div>
      }
      actions={[
        <InternshipApplicationComponent
          key={`internship-application-${internship.id}`}
          internshipId={internship.id}
          sessionId={sessionId}
          organization={organizationName}
          removeInternship={removeInternship}
          applyable={applyable}
          studyLineName={studyLineName}
        />,
      ]}
    >
      <Flex vertical gap={4}>
        <Typography.Text strong title={internship.name} style={truncate}>
          {internship.name}
        </Typography.Text>
        <Typography.Text
          type="secondary"
          title={organizationName}
          style={truncate}
        >
          {organizationName}
        </Typography.Text>
        <Flex gap={4} wrap style={{ marginTop: 8 }}>
          <Tag icon={<ClockCircleOutlined />} bordered={false}>
            {internship.numberOfHours} {t('internshipApplicationComp.hours')}
          </Tag>
          {internship.paid && (
            <Tag color="success" bordered={false}>
              {t('internshipApplicationComp.paid')}
            </Tag>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

export default InternshipOfferCard;
